import { Typography, message } from "antd";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import JwtAxios from "../../../apiServices";
import {
  EXPORT_HOURLY_PRODUCTION_DETAIL_REPORT,
  VIEW_HOURLY_PRODUCTION_DETAIL_REPORT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import { HOURLY_PRODUCTION_VIEW_PERMISSION } from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

// Round 6 (T2): the same last-7-days-ending-today default round 1's mount effect used
// (and HourlyProductionDetailFilterFields.js's Start/End Date initialValues still seed),
// restored now that Task 1's builder-primary path + server paging make page 1 fast
// (R6-6/R6-7: 0.53-0.62s warm through the full request path, see
// D:\cgl-erp\patches\ux-round6\golden\timings.md). Style is intentionally omitted: the
// endpoint returns rows for all styles when style_id is absent.
const getDefaultDateRange = () => ({
  start_date: moment().subtract(6, "days"),
  end_date: moment(),
});

const PAGE_SIZE = 100;

const HourlyProductionDetailReport = () => {
  // States
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  // Round 6 (T2, R6-5/R6-7): server-side pagination state — the endpoint now returns
  // {data, meta} when per_page is sent (Task 1, HourlyProductionDetailFunction.php).
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [fetchError, setFetchError] = useState(null);

  // Round 6 (T2): guard against setState after unmount, needed now that a mount-time
  // fetch exists again (mirrors ProductionDetails/index.js's round-5 pattern).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Fix round 1 (whole-branch review I-4): request-sequence ref — mirrors
  // ProductionDetails/index.js's reqIdRef (git show 29426b894, itself carried over from
  // an earlier "Final fix wave (C1)"). Without it, a slow mount-time fetch resolving
  // after a faster manual Preview (or a faster page change) can overwrite the newer
  // response — observed directly in this task's own evidence capture (the mount request
  // took 3.5s, a subsequent Preview only 1.7s). previewFunction claims a sequence number
  // before its await and only applies a response if it's still the latest one.
  const reqIdRef = useRef(0);

  // Round 6 (T2): the last filter values actually submitted (Preview or the mount
  // default), so a bare pager click can re-run the same filter on a different page
  // without the pager itself knowing about start_date/end_date/style_id.
  const lastFilterValuesRef = useRef({});

  // Antd
  const { Title } = Typography;

  const exportReport = async (filterValues) => {
    const { start_date, end_date, style_id } = filterValues || {};
    if (!start_date && !end_date && !style_id) {
      // Round 3 fix round 1 (cold review REVIEW-T2.md, Important): this branch is what the
      // shared SearchForm's Clear button reaches. common/SearchForm/index.js's Clear handler
      // calls onFinish(null, null, filterName); the "hourly_production_detail" switch case
      // there calls fetchTableData({start_date: null, end_date: null, style_id: null}), and
      // fetchTableData is this page's exportReport via FilterComponent's filterAPI prop.
      // Start/End Date are required fields, so a real Export submit can never reach this
      // branch with all three falsy -- only Clear can. Round 6 (T2): hasSearched is gone
      // (the table renders unconditionally now, R6-7), but Clear must still clear the
      // table and the pagination state so a stale page/total don't survive a Clear.
      // (The pre-existing empty-body POST to the export endpoint below still fires
      // exactly as it did before this fix -- unrelated pre-existing behaviour, out of
      // this fix's scope.)
      setTableData(null);
      setTotal(0);
      setPage(1);
    }
    try {
      setLoading(true);
      const query = `${EXPORT_HOURLY_PRODUCTION_DETAIL_REPORT}`;
      const bodyData = {
        ...(start_date && { start_date: moment(start_date).format("YYYY-MM-DD") }),
        ...(end_date && { end_date: moment(end_date).format("YYYY-MM-DD") }),
        ...(style_id && { style_id }),
      };

      const response = await JwtAxios.post(query, bodyData, {
        responseType: "arraybuffer",
      });

      if (response && response?.data) {
        const outputFilename = `Hourly_Production_Detail_Report_${moment(
          start_date || new Date()
        ).format("YYYY-MM-DD")}_to_${moment(end_date || new Date()).format(
          "YYYY-MM-DD"
        )}.xlsx`;

        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        setLoading(false);
        message.success("Hourly Production Detail Report Exported Successfully");
      } else {
        setLoading(false);
        message.error("Export failed. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      message.error("Export failed. Please try again.");
    }
  };

  // Round 6 (T2, R6-5/R6-7): `pageArg` lets both the Preview button (which calls
  // previewFunction(value) with one argument, so pageArg defaults to 1 -- a filter
  // Preview always resets to page 1) and the antd pager (previewFunction(filterValues,
  // p)) share one fetch path. Sends per_page=100 always, so the endpoint always returns
  // the paginated {data, meta} shape (Task 1, HourlyProductionDetailFunction.php).
  const previewFunction = async (filterValues, pageArg = 1) => {
    // Fix round 1 (whole-branch review I-4): claim this request's sequence number
    // before the await so a later, faster request (mount vs. a manual Preview, or one
    // page change vs. the next) can invalidate this one when it resolves late.
    const myReq = ++reqIdRef.current;
    setFetchError(null);
    setLoading(true);
    lastFilterValuesRef.current = filterValues || {};
    const { start_date, end_date, style_id } = filterValues || {};
    const bodyData = {
      ...(start_date && { start_date: moment(start_date).format("YYYY-MM-DD") }),
      ...(end_date && { end_date: moment(end_date).format("YYYY-MM-DD") }),
      ...(style_id && { style_id }),
      per_page: PAGE_SIZE,
      page: pageArg,
    };

    const query = `${VIEW_HOURLY_PRODUCTION_DETAIL_REPORT}`;
    const response = await postData(query, bodyData);

    // Guard against setState after unmount (the mount-time fetch below can still be
    // in flight if the user navigates away quickly) and against a stale response
    // resolving after a newer request has already been fired (fix round 1).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.code === 200) {
      const rows = response?.data?.data || [];
      const meta = response?.data?.meta || {};
      setLoading(false);
      setTableData(rows);
      setTotal(meta.total || 0);
      setPage(meta.current_page || pageArg);
      message.success(`Loaded ${meta.total ?? rows.length} records.`);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error(response?.message || "Error fetching report preview data.");
    }
  };

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  // Round 6 (T2): restored. Round 3 removed this because the endpoint measured 155.97s
  // warm (199.56s cold) for the seeded last-7-days window on a real data day
  // (ux-round3 T1-timings/timings.md row 13). Task 1's builder-primary rewrite plus
  // server paging now answers page 1 of the same window in 0.53-0.62s warm through the
  // full request path (D:\cgl-erp\patches\ux-round6\golden\timings.md Tables 2-3),
  // comfortably inside R6-6's 2s-warm paginated target, so the mount-time fetch is safe
  // again.
  useEffect(() => {
    previewFunction(getDefaultDateRange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = useMemo(
    () => [
      { title: "Date", dataIndex: "Date", key: "Date", width: 110, fixed: "left" },
      { title: "Hour", dataIndex: "Hour", key: "Hour", width: 70, fixed: "left" },
      { title: "Hour Time Range", dataIndex: "Hour Time Range", key: "Hour Time Range", width: 140 },
      { title: "Line", dataIndex: "Line", key: "Line", width: 120 },
      { title: "Buyer", dataIndex: "Buyer", key: "Buyer", width: 120 },
      { title: "Style", dataIndex: "Style", key: "Style", width: 130 },
      { title: "Sub Style", dataIndex: "Sub Style", key: "Sub Style", width: 130 },
      { title: "Line Chief", dataIndex: "Line Chief", key: "Line Chief", width: 140 },
      { title: "Supervisor", dataIndex: "Supervisor", key: "Supervisor", width: 140 },
      { title: "Operator", dataIndex: "Operator", key: "Operator", width: 140 },
      { title: "Operator Card", dataIndex: "Operator Card", key: "Operator Card", width: 120 },
      { title: "Operator ID Card No", dataIndex: "Operator ID Card No", key: "Operator ID Card No", width: 140 },
      { title: "Operator Department", dataIndex: "Operator Department", key: "Operator Department", width: 150 },
      { title: "Operator Designation", dataIndex: "Operator Designation", key: "Operator Designation", width: 150 },
      { title: "Staff Category", dataIndex: "Staff Category", key: "Staff Category", width: 120 },
      { title: "Employee Type", dataIndex: "Employee Type", key: "Employee Type", width: 120 },
      { title: "Shift Type", dataIndex: "Shift Type", key: "Shift Type", width: 100 },
      { title: "Operation", dataIndex: "Operation", key: "Operation", width: 160 },
      { title: "SAM", dataIndex: "SAM", key: "SAM", width: 80, align: "right" },
      { title: "OB Target Machine SAM", dataIndex: "OB Target Machine SAM", key: "OB Target Machine SAM", width: 160, align: "right" },
      { title: "OB Target Helper SAM", dataIndex: "OB Target Helper SAM", key: "OB Target Helper SAM", width: 160, align: "right" },
      { title: "OB Target Iron SAM", dataIndex: "OB Target Iron SAM", key: "OB Target Iron SAM", width: 150, align: "right" },
      { title: "OB Hourly Target", dataIndex: "OB Hourly Target", key: "OB Hourly Target", width: 140, align: "right" },
      { title: "Daily Line Plan Hourly Target", dataIndex: "Daily Line Plan Hourly Target", key: "Daily Line Plan Hourly Target", width: 190, align: "right" },
      { title: "Efficiency Calc Hourly Target", dataIndex: "Efficiency Calc Hourly Target", key: "Efficiency Calc Hourly Target", width: 190, align: "right" },
      { title: "Current Hour Total Output", dataIndex: "Current Hour Total Output", key: "Current Hour Total Output", width: 170, align: "right" },
      {
        title: "Current Hour Efficiency",
        dataIndex: "Current Hour Efficiency",
        key: "Current Hour Efficiency",
        width: 160,
        align: "right",
        render: (val) => (val !== null && val !== undefined ? `${val}%` : "-"),
      },
      { title: "HPR Quantity", dataIndex: "HPR Quantity", key: "HPR Quantity", width: 120, align: "right" },
      { title: "HPR Previous Quantity", dataIndex: "HPR Previous Quantity", key: "HPR Previous Quantity", width: 160, align: "right" },
    ],
    []
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportReport}
        filterName={"hourly_production_detail"}
        searchButtonText={"Export"}
        defaultOpen
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter, tableData]
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              handleAddItemOpen={exportReport}
              hideAddButton={true}
              disabled={!hasPermission([HOURLY_PRODUCTION_VIEW_PERMISSION])}
              setShowFilter={setShowFilter}
              showFilter={showFilter}
            />
          }
          title={<Title level={5}>Hourly Production Detail Report</Title>}
        />
      }
      content={
        <div className="table" style={{ marginTop: 10 }}>
          {memoizedCustomFilter}
          <ListTable
            size="small"
            loading={loading}
            error={fetchError}
            onRetry={() => previewFunction(lastFilterValuesRef.current, page)}
            emptyText="No records to show yet."
            dataSource={tableData}
            columns={tableColumns}
            rowKey={(row, index) => `${row.Date}_${row.Hour}_${row['Operator Card']}_${row.Operation}_${index}`}
            scroll={{
              x: 3800,
              y: 550,
            }}
            bordered
            sticky
            className="compact-table"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              onChange: (p) => previewFunction(lastFilterValuesRef.current, p),
            }}
          />
        </div>
      }
    />
  );
};

export default HourlyProductionDetailReport;
